import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PropagateLoader } from "react-spinners";

import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import config from "../../Config";

import { encryptData } from "../../Authentication/encryption";
import { decryptData } from "../../Authentication/decryptData";
import { attachMarketingUser } from "../../utils/marketingAttribution";
import PopUp from "../Pop-up/pop-up";
import { GoogleLogin } from "@react-oauth/google";
import { handleGoogleBrowserLogin } from "../../utils/googleLogin";
import AssetConfig from "../../Theme/Assest";

const GoogleButton = React.memo(function GoogleButton({ onSuccess, onError }) {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const update = () => {
      const w = containerRef.current?.offsetWidth || 0;
      setWidth(Math.max(200, Math.min(400, Math.floor(w))));
    };

    update();
    window.addEventListener("resize", update);

    return () => window.removeEventListener("resize", update);
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        width: "100%",
        height: 44,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {width > 0 && (
        <GoogleLogin
          onSuccess={onSuccess}
          onError={onError}
          useOneTap={false}
          theme="outline"
          shape="rectangular"
          size="large"
          text="continue_with"
          width={width}
        />
      )}
    </Box>
  );
});

export default function SignInPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [isPhoneSignIn, setIsPhoneSignIn] = useState(true);
  const [isEmailSignIn, setIsEmailSignIn] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [isOtpSent, setIsOtpSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [timer, setTimer] = useState(30);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  const [showSignupPopup, setShowSignupPopup] = useState(false);
  const [popupContact, setPopupContact] = useState("");
  const [popupEmail, setPopupEmail] = useState("");

  useEffect(() => {
    const contact = searchParams.get("contact");
    const emailParam = searchParams.get("email");

    if (contact) {
      setPhoneNumber(contact.replace(/\D/g, "").slice(0, 10));
      setIsPhoneSignIn(true);
      setIsEmailSignIn(false);
    } else if (emailParam) {
      setEmail(emailParam);
      setIsPhoneSignIn(false);
      setIsEmailSignIn(true);
    }
  }, [searchParams]);

  useEffect(() => {
    let interval;

    if (isTimerRunning && timer > 0) {
      interval = setInterval(() => {
        setTimer((previous) => previous - 1);
      }, 1000);
    }

    if (timer === 0) {
      setIsTimerRunning(false);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isTimerRunning, timer]);

  const openSignupPopup = (contact, emailValue) => {
    setPopupContact(contact || "");
    setPopupEmail(emailValue || "");
    setShowSignupPopup(true);
  };

  const handleCreateAccount = () => {
    const query = isPhoneSignIn
      ? `?contact=${encodeURIComponent(popupContact)}`
      : `?email=${encodeURIComponent(popupEmail)}`;

    navigate(`/signup${query}`);
  };

  const handlePhoneSignIn = () => {
    setIsPhoneSignIn(true);
    setIsEmailSignIn(false);
    setIsOtpSent(false);
    setOtp("");
    setError("");
    setTimer(30);
    setIsTimerRunning(false);
  };

  const handleEmailSignIn = () => {
    setIsEmailSignIn(true);
    setIsPhoneSignIn(false);
    setIsOtpSent(false);
    setOtp("");
    setError("");
    setTimer(30);
    setIsTimerRunning(false);
  };

  const handleGetOtp = async () => {
    if (loading) return;

    setError("");

    const cleanPhone = phoneNumber.replace(/\D/g, "");
    const emailValue = email.trim();

    if (isPhoneSignIn && cleanPhone.length !== 10) {
      setError("Please enter a valid 10-digit phone number.");
      return;
    }

    if (
      isEmailSignIn &&
      (!emailValue || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(emailValue))
    ) {
      setError("Please enter a valid email address.");
      return;
    }

    try {
      setLoading(true);

      const endpoint = isPhoneSignIn
        ? "/user/mobileSigninOtp"
        : "/user/emailSigninOtp";

      const payload = isPhoneSignIn
        ? { contact: cleanPhone }
        : { email: emailValue };

      await axios.post(`${config.apiBaseUrl}${endpoint}`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      if (isPhoneSignIn) setPhoneNumber(cleanPhone);
      if (isEmailSignIn) setEmail(emailValue);

      setIsOtpSent(true);
      setOtp("");
      setTimer(30);
      setIsTimerRunning(true);
    } catch (err) {
      const status = err.response?.status;
      const message = String(err.response?.data?.message || "");

      const userNotFound =
        status === 404 ||
        /user does not exist|user not found|not registered/i.test(message);

      if (userNotFound) {
        openSignupPopup(
          isPhoneSignIn ? cleanPhone : "",
          isEmailSignIn ? emailValue : "",
        );
        return;
      }

      setError(
        message || "There was an issue sending the OTP. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitOtp = async () => {
    if (loading) return;

    setError("");

    if (!otp.trim()) {
      setError("Please enter the OTP.");
      return;
    }

    if (otp.trim().length < 4) {
      setError("Please enter a valid OTP.");
      return;
    }

    const signInData = isEmailSignIn
      ? { email: email.trim(), otp: otp.trim() }
      : { contact: phoneNumber.replace(/\D/g, ""), otp: otp.trim() };

    try {
      setLoading(true);

      const encryptedSignInData = encryptData(signInData);

      const response = await axios.post(
        `${config.apiBaseUrl}/${config.userSignIn}`,
        { encryptedData: encryptedSignInData },
        { headers: { "Content-Type": "application/json" } },
      );

      if (response.status !== 200) {
        setError(`Error: ${response.status}`);
        return;
      }

      const decryptedData = decryptData(response.data);

      if (!decryptedData?.userDoc || !decryptedData?.newToken) {
        setError("Failed to decode the response or missing required data.");
        return;
      }

      const userId = decryptedData.userDoc.userId;
      const userEmail =
        decryptedData.userDoc.email || (isEmailSignIn ? email.trim() : null);
      const userContact =
        decryptedData.userDoc.contact ||
        decryptedData.userDoc.phone ||
        (isPhoneSignIn ? phoneNumber.replace(/\D/g, "") : null);

      if (userId) sessionStorage.setItem("userId", userId);
      sessionStorage.setItem("newToken", decryptedData.newToken);

      if (userEmail) sessionStorage.setItem("userEmail", userEmail);
      if (userContact) sessionStorage.setItem("userContact", userContact);

      await attachMarketingUser({
        userId,
        email: userEmail,
        contact: userContact,
      });

      setIsTimerRunning(false);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      const message = String(err.response?.data?.message || "");

      const userNotFound =
        status === 404 ||
        /user does not exist|user not found|not registered/i.test(message);

      if (userNotFound) {
        openSignupPopup(
          isPhoneSignIn ? phoneNumber.replace(/\D/g, "") : "",
          isEmailSignIn ? email.trim() : "",
        );
        return;
      }

      setError(
        status === 498
          ? "Invalid OTP. Please try again."
          : message ||
              "There was an issue during submission. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (loading || timer > 0) return;
    await handleGetOtp();
  };

  const handleChangeDetails = () => {
    setIsOtpSent(false);
    setOtp("");
    setError("");
    setTimer(30);
    setIsTimerRunning(false);
  };

  const handleGoogleSuccess = useCallback(
    async (credentialResponse) => {
      setLoading(true);
      setError("");

      await handleGoogleBrowserLogin({
        credential: credentialResponse.credential,
        onSuccess: () => navigate("/"),
        onError: (message) => setError(message),
      });

      setLoading(false);
    },
    [navigate],
  );

  const handleGoogleError = useCallback(() => {
    setError("Google login failed. Please try again.");
  }, []);

  const isPhoneValid = phoneNumber.replace(/\D/g, "").length === 10;
  const isEmailValid =
    email.trim() !== "" && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());

  const isOtpButtonDisabled = isPhoneSignIn
    ? !isPhoneValid || loading
    : !isEmailValid || loading;

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      height: 55,
      borderRadius: 0,
      "& fieldset": { borderColor: "#d8d2c9" },
      "&:hover fieldset": { borderColor: "#977743" },
      "&.Mui-focused fieldset": {
        borderColor: "#171717",
        borderWidth: 1,
      },
    },
    "& input": { fontSize: 13 },
  };

  const primaryButtonSx = {
    height: 54,
    borderRadius: 0,
    background: "#171717",
    color: "#fff",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.16em",
    textTransform: "uppercase",
    boxShadow: "none",
    "&:hover": { background: "#977743", boxShadow: "none" },
    "&:disabled": { background: "#302f2d", color: "#fff" },
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "#f7f4ef",
        color: "#171717",
      }}
    >
      <Navbar />

      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: { xs: 5, sm: 7, md: 9 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <Container maxWidth="sm" sx={{ px: { xs: 0, sm: 2 } }}>
          <Box
            sx={{
              width: "100%",
              maxWidth: 540,
              mx: "auto",
              background: "#fff",
              border: "1px solid #ddd7ce",
              boxShadow: "0 20px 70px rgba(23,23,23,0.07)",
              px: { xs: 2.5, sm: 5, md: 6 },
              py: { xs: 3.5, sm: 5, md: 5.5 },
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  width: 85,
                  height: 85,
                  mx: "auto",
                  mb: 2.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "#f7f4ef",
                  border: "1px solid #ddd7ce",
                }}
              >
                <Box
                  component="img"
                  src={AssetConfig.logoLight}
                  alt="Hindustan Wardrobe"
                  sx={{ width: 85, height: 85, objectFit: "contain" }}
                />
              </Box>

              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={1.2}
                sx={{ mb: 1.2 }}
              >
                <Box sx={{ width: 25, height: 1, background: "#977743" }} />
                <Typography
                  sx={{
                    fontSize: 8,
                    fontWeight: 700,
                    letterSpacing: "0.22em",
                    textTransform: "uppercase",
                    color: "#977743",
                  }}
                >
                  Hindustan Wardrobe
                </Typography>
                <Box sx={{ width: 25, height: 1, background: "#977743" }} />
              </Stack>

              <Typography
                component="h1"
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: { xs: 34, sm: 40 },
                  fontWeight: 400,
                  lineHeight: 1.1,
                  letterSpacing: "-0.035em",
                }}
              >
                Welcome back
              </Typography>

              <Typography
                sx={{
                  mt: 1.2,
                  color: "#77716a",
                  fontSize: 12,
                  lineHeight: 1.7,
                }}
              >
                Sign in to continue to your wardrobe.
              </Typography>
            </Box>

            <Stack spacing={1.5} sx={{ maxWidth: 400, mx: "auto" }}>
              <GoogleButton
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
              />

              <Button
                fullWidth
                variant="outlined"
                startIcon={
                  isPhoneSignIn ? (
                    <EmailOutlinedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <PhoneOutlinedIcon sx={{ fontSize: 18 }} />
                  )
                }
                onClick={isPhoneSignIn ? handleEmailSignIn : handlePhoneSignIn}
                sx={{
                  height: 50,
                  borderRadius: 0,
                  borderColor: "#d8d2c9",
                  color: "#171717",
                  background: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  "&:hover": { borderColor: "#171717", background: "#faf8f5" },
                }}
              >
                {isPhoneSignIn ? "Continue with Email" : "Continue with Phone"}
              </Button>
            </Stack>

            <Divider
              sx={{
                my: 3.5,
                "&::before, &::after": { borderColor: "#e5e0d8" },
              }}
            >
              <Typography
                sx={{
                  px: 1,
                  fontSize: 9,
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  color: "#99938b",
                }}
              >
                OR
              </Typography>
            </Divider>

            {!isOtpSent ? (
              <Box>
                <Typography
                  sx={{
                    mb: 1,
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.09em",
                    textTransform: "uppercase",
                    color: "#4e4a45",
                  }}
                >
                  {isPhoneSignIn ? "Mobile Number" : "Email Address"}
                </Typography>

                {isPhoneSignIn ? (
                  <TextField
                    fullWidth
                    placeholder="Enter your 10-digit number"
                    type="tel"
                    value={phoneNumber}
                    onChange={(e) => {
                      setPhoneNumber(
                        e.target.value.replace(/\D/g, "").slice(0, 10),
                      );
                      setError("");
                    }}
                    InputProps={{
                      startAdornment: (
                        <Typography
                          sx={{ mr: 1, fontSize: 13, color: "#77716a" }}
                        >
                          +91
                        </Typography>
                      ),
                    }}
                    sx={fieldSx}
                  />
                ) : (
                  <TextField
                    fullWidth
                    placeholder="Enter your email address"
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError("");
                    }}
                    sx={fieldSx}
                  />
                )}

                {error && (
                  <Typography
                    sx={{
                      mt: 1.2,
                      fontSize: 11,
                      lineHeight: 1.5,
                      color: "#a33b32",
                    }}
                  >
                    {error}
                  </Typography>
                )}

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleGetOtp}
                  disabled={isOtpButtonDisabled}
                  sx={{ ...primaryButtonSx, mt: 2.5 }}
                >
                  {loading ? (
                    <PropagateLoader color="#fff" size={7} />
                  ) : (
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                      spacing={1}
                    >
                      <Typography
                        component="span"
                        sx={{
                          fontSize: 10,
                          fontWeight: 700,
                          letterSpacing: "0.16em",
                        }}
                      >
                        Get OTP
                      </Typography>
                      <ArrowForwardRoundedIcon sx={{ fontSize: 16 }} />
                    </Stack>
                  )}
                </Button>
              </Box>
            ) : (
              <Box>
                <Box sx={{ textAlign: "center", mb: 3 }}>
                  <Typography
                    sx={{
                      fontSize: 9,
                      fontWeight: 700,
                      letterSpacing: "0.2em",
                      textTransform: "uppercase",
                      color: "#977743",
                      mb: 1,
                    }}
                  >
                    Secure Verification
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: "Georgia, serif",
                      fontSize: 28,
                      fontWeight: 400,
                      lineHeight: 1.2,
                    }}
                  >
                    Enter your OTP
                  </Typography>
                  <Typography
                    sx={{
                      mt: 1,
                      fontSize: 11,
                      lineHeight: 1.7,
                      color: "#77716a",
                    }}
                  >
                    We sent a verification code to your{" "}
                    {isPhoneSignIn ? "mobile number" : "email address"}.
                  </Typography>
                </Box>

                <TextField
                  fullWidth
                  placeholder="Enter 6-digit OTP"
                  type="tel"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value.replace(/\D/g, "").slice(0, 6));
                    setError("");
                  }}
                  inputProps={{ maxLength: 6, inputMode: "numeric" }}
                  sx={{
                    ...fieldSx,
                    "& .MuiOutlinedInput-root": {
                      ...fieldSx["& .MuiOutlinedInput-root"],
                      height: 60,
                    },
                    "& input": {
                      textAlign: "center",
                      letterSpacing: "0.45em",
                      fontSize: 19,
                      fontWeight: 600,
                    },
                  }}
                />

                {error && (
                  <Typography
                    sx={{
                      mt: 1.2,
                      textAlign: "center",
                      fontSize: 11,
                      lineHeight: 1.5,
                      color: "#a33b32",
                    }}
                  >
                    {error}
                  </Typography>
                )}

                <Box
                  sx={{
                    minHeight: 35,
                    mt: 1.5,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  {timer > 0 ? (
                    <Typography
                      sx={{
                        fontSize: 10,
                        color: "#77716a",
                        letterSpacing: "0.05em",
                      }}
                    >
                      Resend OTP{" "}
                      <Box
                        component="span"
                        sx={{ color: "#171717", fontWeight: 700 }}
                      >
                        in {timer}s
                      </Box>
                    </Typography>
                  ) : (
                    <Button
                      onClick={handleResendOtp}
                      disabled={loading}
                      sx={{
                        minWidth: "auto",
                        p: 0,
                        color: "#977743",
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: "0.1em",
                        textTransform: "uppercase",
                        "&:hover": {
                          background: "transparent",
                          color: "#171717",
                        },
                      }}
                    >
                      Resend OTP
                    </Button>
                  )}
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSubmitOtp}
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {loading ? (
                    <PropagateLoader color="#fff" size={7} />
                  ) : (
                    "Verify & Sign In"
                  )}
                </Button>

                <Button
                  fullWidth
                  variant="text"
                  onClick={handleChangeDetails}
                  startIcon={<ArrowBackRoundedIcon sx={{ fontSize: 15 }} />}
                  sx={{
                    mt: 1,
                    height: 42,
                    borderRadius: 0,
                    color: "#77716a",
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: "0.1em",
                    textTransform: "uppercase",
                    "&:hover": { background: "#f7f4ef", color: "#171717" },
                  }}
                >
                  Change details
                </Button>
              </Box>
            )}

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: "1px solid #eee9e2",
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 11, color: "#77716a" }}>
                Don't have an account?{" "}
                <Link
                  href="/signup"
                  underline="none"
                  sx={{
                    color: "#171717",
                    fontWeight: 700,
                    "&:hover": { color: "#977743" },
                  }}
                >
                  Create one
                </Link>
              </Typography>
            </Box>

            <Typography
              sx={{
                mt: 3,
                textAlign: "center",
                fontSize: 8,
                lineHeight: 1.7,
                color: "#aaa39a",
                letterSpacing: "0.04em",
              }}
            >
              Secure access to your Hindustan Wardrobe account.
            </Typography>
          </Box>
        </Container>
      </Box>

      <Footer />

      <PopUp
        open={showSignupPopup}
        setOpen={setShowSignupPopup}
        title="Create an account?"
        description={
          isPhoneSignIn
            ? "We couldn't find an account with this mobile number. Would you like to create one?"
            : "We couldn't find an account with this email address. Would you like to create one?"
        }
        primaryText="Create Account"
        secondaryText="Cancel"
        onPrimary={handleCreateAccount}
      />
    </Box>
  );
}